import { useCallback, useEffect, useRef, useState } from 'react'
import API from '../../../services/API'

// The board asks the server for one group (vehicles / bags / other assets),
// one set of levels and one page at a time - organisations can have
// thousands of each, and rendering them all is what made the board slow.
// GET /makeready/board also returns counts for every group and level, so the
// tabs, chips, donut and Needs Attention panel stay whole-fleet while the
// grid only holds what's been paged in. The restock queue, which needs item
// lists, stays on useTrackedItems.

export const PAGE_SIZE = 60

const EMPTY_COUNTS = { vehicles: {}, bags: {}, assets: {} }

// Server rows -> the shape the cards use.
const toBoardItem = (row) => {
  // Stored Vehicle/Asset makeReadyStatus (null for untracked/never checked).
  const s = row.status || {}
  return {
    id: row._id,
    kind: row.kind,
    group: row.group,
    label: row.label,
    subLabel: row.subLabel,
    category: row.category,
    subCategory: row.subCategory,
    tracked: row.tracked,
    level: row.level,
    loadListId: row.makeReadyLoadList || null,
    suggestedLoadList: row.suggestedLoadList || null,
    location: row.location || [],
    lastCheckedAt: s.lastCheckedAt || null,
    missingCount: s.missingCount || 0,
    expiredCount: s.expiredCount || 0,
    expiringSoonCount: s.expiringSoonCount || 0,
    offListCount: s.offListCount || 0,
    nextExpiryAt: s.nextExpiryAt || null,
  }
}

const useMakeReadyBoard = ({ group, levels, search, sort, store = '', enabled = true }) => {
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [counts, setCounts] = useState(EMPTY_COUNTS)
  const [loadLists, setLoadLists] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  // Only the latest request may write state - a slow page for the previous
  // tab/filter must not land on top of the current one.
  const requestId = useRef(0)
  const levelsKey = levels.join(',')

  const fetchPage = useCallback(
    (offset) => {
      const id = ++requestId.current
      const params = new URLSearchParams({
        group,
        sort,
        offset: String(offset),
        limit: String(PAGE_SIZE),
      })
      if (levelsKey) params.set('levels', levelsKey)
      if (search.trim()) params.set('search', search.trim())
      // Kit-store drill-in - scopes rows AND counts to that store's contents.
      if (store) params.set('store', store)
      return API.get(`/makeready/board?${params.toString()}`).then((res) => {
        if (id !== requestId.current) return null
        if (res.status !== 200) throw new Error('board')
        return res.data
      })
    },
    [group, levelsKey, search, sort, store],
  )

  useEffect(() => {
    // The Stores tab renders from /kitstores instead - no board page needed.
    if (!enabled) return
    setLoading(true)
    setFetchError(false)
    fetchPage(0)
      .then((data) => {
        if (!data) return
        setItems((data.items || []).map(toBoardItem))
        setTotal(data.total || 0)
        setCounts(data.counts || EMPTY_COUNTS)
        setLoadLists(data.loadLists || [])
        setLoading(false)
      })
      .catch(() => {
        setFetchError(true)
        setLoading(false)
      })
  }, [fetchPage, reloadKey, enabled])

  const loadMore = useCallback(() => {
    setLoadingMore(true)
    fetchPage(items.length)
      .then((data) => {
        if (!data) return
        setItems((prev) => [...prev, ...(data.items || []).map(toBoardItem)])
        setTotal(data.total || 0)
        setCounts(data.counts || EMPTY_COUNTS)
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoadingMore(false))
  }, [fetchPage, items.length])

  const refresh = useCallback(() => setReloadKey((k) => k + 1), [])

  return {
    items,
    total,
    counts,
    loadLists,
    loading,
    loadingMore,
    fetchError,
    loadMore,
    refresh,
  }
}

export default useMakeReadyBoard
